import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { StyledButtonSalvar } from "../styles";
import { Mint } from "./Mint";
import { Purchase } from "./Purchase";
import { Collaterals } from "./Collaterals";
import {
  Label,
  IndexContainer,
  Input,
  ButtonContainer,
  CollateralItem,
  CollateralList,
  Container,
  Row,
  ShowInput,
} from "./styles.js";

const REQUIRED_CHAIN_ID_HEX = "0x539"; // 1337 (Ganache)
const REQUIRED_CHAIN_ID_DEC = "1337";

function AddressSelect({ accounts, value, onChange, disabled }) {
  // componente que NÃO permite digitação, apenas seleção
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || accounts.length === 0}
      style={{
        padding: "8px",
        borderRadius: "4px",
        border: "1px solid var(--border-default)",
        width: "100%",
        fontSize: "14px",
      }}
    >
      {accounts.map((account) => (
        <option key={account} value={account}>
          {account.substring(0, 6)}...{account.substring(account.length - 4)}
        </option>
      ))}
    </select>
  );
}

const CryptoMoeda = () => {
  const [amount, setAmount] = useState("5");
  const [serialNumber, setSerialNumber] = useState(
    "8BEzRTxaQ2t3wLBosmtZ6ahAgGKdyUqEg6NoqyBHS7R1aNM6fKE"
  );
  const [value, setValue] = useState(5);
  const [credit, setCredit] = useState("0.000000000000000001");

  // esse address abaixo é para consultar colaterais (se quiser, também podemos trocar por select)
  const [address, setAddress] = useState("");

  const [collaterals, setCollaterals] = useState([]);

  // MetaMask
  const [isConnected, setIsConnected] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountForMint, setSelectedAccountForMint] = useState("");
  const [selectedAccountForSale, setSelectedAccountForSale] = useState("");
  const [chainId, setChainId] = useState("");

  const onRightNetwork = chainId === REQUIRED_CHAIN_ID_HEX || chainId === REQUIRED_CHAIN_ID_DEC;

  // helpers para exibição/validação
  const readyToTransact = isConnected && onRightNetwork && accounts.length > 0;
  const mintDisabled = !readyToTransact || !selectedAccountForMint;
  const saleDisabled = !readyToTransact || !selectedAccountForSale;

  // Carrega estado inicial + listeners da MetaMask
  useEffect(() => {
    const eth = window?.ethereum;
    if (!eth) return;

    const init = async () => {
      try {
        const accs = await eth.request({ method: "eth_accounts" });
        const cid = await eth.request({ method: "eth_chainId" });
        setChainId(cid);
        if (accs?.length) {
          setIsConnected(true);
          setAccounts(accs);
          setSelectedAccountForMint(accs[0]);
          setSelectedAccountForSale(accs[0]);
        }
      } catch (e) {
        console.error("init metamask:", e);
      }
    };

    init();

    const handleAccountsChanged = (accs) => {
      setAccounts(accs);
      if (accs.length === 0) {
        setIsConnected(false);
        setSelectedAccountForMint("");
        setSelectedAccountForSale("");
      } else {
        setIsConnected(true);
        setSelectedAccountForMint(accs[0]);
        setSelectedAccountForSale(accs[0]);
      }
    };

    const handleChainChanged = (cid) => {
      setChainId(cid);
    };

    eth.on?.("accountsChanged", handleAccountsChanged);
    eth.on?.("chainChanged", handleChainChanged);

    return () => {
      eth.removeListener?.("accountsChanged", handleAccountsChanged);
      eth.removeListener?.("chainChanged", handleChainChanged);
    };
  }, []);

  const connectWallet = async () => {
    try {
      const eth = window?.ethereum;
      if (!eth) {
        Swal.fire({
          title: "MetaMask não encontrado!",
          text: "Instale a extensão MetaMask no navegador.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      const accs = await eth.request({ method: "eth_requestAccounts" });
      const cid = await eth.request({ method: "eth_chainId" });
      setChainId(cid);

      if (accs.length > 0) {
        setIsConnected(true);
        setAccounts(accs);
        setSelectedAccountForMint(accs[0]);
        setSelectedAccountForSale(accs[0]);

        Swal.fire({
          title: "Conectado!",
          text: "Carteira MetaMask conectada com sucesso!",
          icon: "success",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Erro ao conectar carteira:", error);
      Swal.fire({
        title: "Erro!",
        text: "Falha ao conectar com a MetaMask.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const ensureRightChain = async () => {
    // força a rede 1337 (Ganache)
    const eth = window?.ethereum;
    if (!eth) return false;
    try {
      if (!onRightNetwork) {
        await eth.request({
          method: "wallet_switchEthereumChain",
          params: [{ chainId: REQUIRED_CHAIN_ID_HEX }],
        });
      }
      return true;
    } catch (e) {
      // se a rede não existe na carteira, tentar adicionar
      if (e?.code === 4902) {
        try {
          await eth.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: REQUIRED_CHAIN_ID_HEX,
                chainName: "Ganache Local",
                rpcUrls: ["http://localhost:7545"],
                nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
              },
            ],
          });
          return true;
        } catch (err) {
          console.error("add chain failed:", err);
        }
      }
      console.error("switch chain failed:", e);
      return false;
    }
  };

  const mintMoeda = async () => {
    if (!isConnected) {
      Swal.fire({ title: "Erro!", text: "Conecte a carteira primeiro!", icon: "error" });
      return;
    }
    const ok = await ensureRightChain();
    if (!ok) {
      Swal.fire({
        title: "Rede incorreta",
        text: "Troque para a rede Ganache (1337) e tente novamente.",
        icon: "warning",
      });
      return;
    }

    try {
      const retorno = await Mint(selectedAccountForMint, amount, serialNumber, value);
      console.log("Mint retorno:", retorno);
      Swal.fire({ title: "Sucesso!", text: "Moeda criada com sucesso!", icon: "success" });
    } catch (err) {
      console.error("Erro ao cunhar moeda:", err);
      Swal.fire({ title: "Erro", text: "Falha ao cunhar.", icon: "error" });
    }
  };

  const PurchaseMoeda = async () => {
    if (!isConnected) {
      Swal.fire({ title: "Erro!", text: "Conecte a carteira primeiro!", icon: "error" });
      return;
    }
    const ok = await ensureRightChain();
    if (!ok) {
      Swal.fire({
        title: "Rede incorreta",
        text: "Troque para a rede Ganache (1337) e tente novamente.",
        icon: "warning",
      });
      return;
    }

    try {
      const retorno = await Purchase(selectedAccountForSale, credit);
      console.log("Purchase retorno:", retorno);
      Swal.fire({ title: "Sucesso!", text: "Compra realizada com sucesso!", icon: "success" });
    } catch (err) {
      console.error("Erro ao comprar:", err);
      Swal.fire({ title: "Erro", text: "Falha na compra.", icon: "error" });
    }
  };

  const GetCollaterals = async () => {
    try {
      const res = await Collaterals(address);
      // espera-se que Collaterals retorne [serials[], values[]]
      const combined = res[0].map((serial, i) => ({
        serialNumber: serial,
        value: res[1][i],
      }));
      setCollaterals(combined);
    } catch (err) {
      console.error("Erro ao listar colaterais:", err);
    }
  };

  return (
    <IndexContainer>
      {/* Conexão */}
      <Container>
        <h2>Conectar Carteira</h2>
        {!isConnected ? (
          <ButtonContainer>
            <StyledButtonSalvar onClick={connectWallet}>Conectar MetaMask</StyledButtonSalvar>
          </ButtonContainer>
        ) : (
          <div>
            <p>
              <strong>Carteira conectada:</strong>{" "}
              {accounts[0]?.substring(0, 6)}...{accounts[0]?.substring(accounts[0]?.length - 4)}
            </p>
            <p>
              <strong>Chain:</strong> {chainId} {onRightNetwork ? "(ok)" : "(trocar p/ 0x539)"}
            </p>
          </div>
        )}
      </Container>

      {/* Cunhar Moeda Digital */}
      <Container>
        <h2>Cunhar Moeda Digital</h2>

        <Row>
          <Label>Endereço:</Label>
          {isConnected ? (
            <AddressSelect
              accounts={accounts}
              value={selectedAccountForMint}
              onChange={setSelectedAccountForMint}
              disabled={!isConnected}
            />
          ) : (
            <ShowInput type="text" value="Conecte uma carteira primeiro" disabled readOnly />
          )}
        </Row>

        <Row>
          <Label htmlFor="amount">Quantidade:</Label>
          <ShowInput
            type="number"
            id="amount"
            name="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Row>

        <Row>
          <Label htmlFor="serialNumber">Número de Série:</Label>
          <ShowInput
            type="text"
            id="serialNumber"
            name="serialNumber"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
          />
        </Row>

        <Row>
          <Label htmlFor="value">Valor:</Label>
          <ShowInput
            type="number"
            id="value"
            name="value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Row>

        <ButtonContainer>
          <StyledButtonSalvar onClick={mintMoeda} disabled={mintDisabled}>
            Cunhar Moeda
          </StyledButtonSalvar>
        </ButtonContainer>
      </Container>

      {/* Realizar uma Venda */}
      <Container>
        <h2>Realizar uma Venda</h2>

        <Row>
          <Label htmlFor="creditValue">Créditos a serem compensados:</Label>
          <ShowInput
            type="number"
            id="creditValue"
            name="creditValue"
            value={credit}
            onChange={(e) => setCredit(e.target.value)}
          />
        </Row>

        <Row>
          <Label>Endereço da Carteira:</Label>
          {isConnected ? (
            <AddressSelect
              accounts={accounts}
              value={selectedAccountForSale}
              onChange={setSelectedAccountForSale}
              disabled={!isConnected}
            />
          ) : (
            <ShowInput type="text" value="Conecte uma carteira primeiro" disabled readOnly />
          )}
        </Row>

        <ButtonContainer>
          <StyledButtonSalvar onClick={PurchaseMoeda} disabled={saleDisabled}>
            Comprar
          </StyledButtonSalvar>
        </ButtonContainer>
      </Container>

      {/* Lista de lastros */}
      <Container>
        <h2>Listar lastros</h2>
        <Row>
          {collaterals.length > 0 && (
            <CollateralList>
              <h3>Colaterais:</h3>
              {collaterals.map((c, idx) => (
                <CollateralItem key={idx}>
                  Serial: {c.serialNumber}, Valor: {c.value}
                </CollateralItem>
              ))}
            </CollateralList>
          )}
        </Row>
      </Container>

      {/* Consulta por endereço (aqui ainda deixei editável; se quiser, troco por select) */}
      <Container>
        <Row>
          <Label htmlFor="collateralAddress">Endereço:</Label>
          <ShowInput
            type="text"
            id="collateralAddress"
            name="collateralAddress"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="0x..."
          />
        </Row>

        <ButtonContainer>
          <StyledButtonSalvar onClick={GetCollaterals}>Listar</StyledButtonSalvar>
        </ButtonContainer>
      </Container>
    </IndexContainer>
  );
};

export default CryptoMoeda;
